import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import Footer from '../components/Footer';
import logo from '../assets/logo/logo_colour.jpeg';
import { Toaster } from "@/components/ui/sonner"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Menu } from 'lucide-react';

export const RootLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: authData } = useQuery({
    queryKey: ['authCheck'],
    queryFn: async () => {
      const response = await fetch('/api/auth/logged-in');
      if (!response.ok) {
        throw new Error('Failed to check authentication status');
      }
      return response.json();
    },
  });

  useEffect(() => {
    if (authData?.loggedIn) {
      navigate('/app');
    }
  }, [authData, navigate]);

  const isActive = (path: string) => {
    return location.pathname === path ? 'font-bold text-primary' : 'text-muted-foreground hover:text-foreground transition-colors';
  };

  const navLinks = [
    { to: '/', label: 'Home' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header/Navigation */}
      <header className="border-b sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/" className="font-bold text-xl flex items-center gap-2">
              <div className="bg-primary text-primary-foreground size-8 rounded-full flex items-center justify-center overflow-hidden">
                <img src={logo} alt="TytoBio" className="size-8 object-cover" />
              </div>
              <span>TytoBio</span>
            </Link>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className={isActive(link.to)}>
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop auth buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/login">Log in</Link>
            </Button>
            <Button asChild>
              <Link to="/register">Sign up</Link>
            </Button>
          </div>

          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="size-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-4 px-4 pt-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`text-lg ${isActive(link.to)}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="border-t pt-4 mt-2 flex flex-col gap-3">
                  <Button variant="outline" asChild className="w-full" onClick={() => setMobileOpen(false)}>
                    <Link to="/login">Log in</Link>
                  </Button>
                  <Button asChild className="w-full" onClick={() => setMobileOpen(false)}>
                    <Link to="/register">Sign up</Link>
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
        <Toaster />
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};
